import api from './api'
import { getLocationDisplayName } from '@/utils/locationName'

/**
 * Servicio para manejar las operaciones relacionadas con ubicaciones
 */
class LocationsService {
    /**
     * Obtiene todas las ubicaciones (comunas y sedes)
     * @returns {Promise<Array>} Lista de ubicaciones con sus comunas y regiones
     */
    async getLocations() {
        try {
            const response = await api.get('/locations')
            return response.data
        } catch (error) {
            console.error('Error fetching locations:', error)
            throw error
        }
    }

    /**
     * Obtiene las ubicaciones específicas de PDVx
     * @returns {Promise<Array>} Lista de ubicaciones de PDVx con sus comunas y regiones
     */
    async getPdvXLocations() {
        try {
            const response = await api.get('/locations?pdvX=true')
            return response.data
        } catch (error) {
            console.error('Error fetching PDVx locations:', error)
            throw error
        }
    }

    /**
     * Obtiene las comunas habilitadas para un programa específico
     * @param {string} programa - Código del programa (ej: 'Presencial')
     * @param {string} year - Año del programa (ej: '4')
     * @returns {Promise<Array>} Lista de programas con sus ubicaciones habilitadas
     */
    async getComunasByProgram(programa = 'Presencial', year = '4') {
        try {
            const response = await api.get(`/programs/meta/${programa}?year=${year}`)
            return response.data
        } catch (error) {
            console.error('Error fetching comunas by program:', error)
            throw error
        }
    }

    /**
     * Obtiene las comunas únicas de las ubicaciones
     * @param {Array} locations - Lista de ubicaciones
     * @returns {Array} Lista de comunas únicas
     */
    getUniqueComunas(locations) {
        const comunasMap = new Map()
        
        locations.forEach(location => {
            if (location.Commune && !comunasMap.has(location.Commune.id)) {
                comunasMap.set(location.Commune.id, {
                    id: location.Commune.id,
                    name: location.Commune.name,
                    regionId: location.Commune.regionId,
                    regionName: location.Region ? location.Region.name : ''
                })
            }
        })
        
        return Array.from(comunasMap.values()).sort((a, b) => a.name.localeCompare(b.name))
    }

    /**
     * Obtiene las comunas únicas desde los datos del programa
     * @param {Array} programData - Datos del programa con ubicaciones
     * @param {Array} allLocations - Todas las ubicaciones para obtener información de comunas
     * @returns {Array} Lista de comunas únicas
     */
    getUniqueComunasFromProgram(programData, allLocations = []) {
        
        const comunasMap = new Map()
        
        if (programData && programData.length > 0) {
            const program = programData[0] // Tomamos el primer programa
            
            // Verificar diferentes posibles estructuras de datos
            let locations = null;
            if (program.Locations) {
                locations = program.Locations;
            } else if (program.locations) {
                locations = program.locations;
            } else if (program.location) {
                locations = Array.isArray(program.location) ? program.location : [program.location];
            }
            if (locations && locations.length > 0) {
                locations.forEach((location) => {
                    
                    // Verificar diferentes posibles nombres de campo para CommuneId
                    let communeId = null;
                    let communeName = null;
                    let regionName = null;
                    
                    if (location.CommuneId) {
                        communeId = location.CommuneId;
                    } else if (location.communeId) {
                        communeId = location.communeId;
                    } else if (location.Commune && location.Commune.id) {
                        communeId = location.Commune.id;
                    } else if (location.commune && location.commune.id) {
                        communeId = location.commune.id;
                    }
                    
                    // Obtener información de la comuna directamente de la ubicación
                    if (location.Commune) {
                        communeName = location.Commune.name;
                    } else if (location.commune) {
                        communeName = location.commune.name;
                    }
                    
                    // Obtener información de la región directamente de la ubicación
                    if (location.Region) {
                        regionName = location.Region.name;
                    } else if (location.region) {
                        regionName = location.region.name;
                    }
                    
                    if (communeId && !comunasMap.has(communeId)) {
                        
                        // Si tenemos la información completa de la comuna, usarla directamente
                        if (communeName) {
                            const comunaInfo = {
                                id: communeId,
                                name: communeName,
                                regionId: location.RegionId || location.regionId,
                                regionName: regionName || ''
                            }
                            comunasMap.set(communeId, comunaInfo)
                        } else {
                            // Buscar información de la comuna desde las ubicaciones completas
                            const comunaInfo = this.getComunaInfoFromAllLocations(communeId, allLocations)
                            if (comunaInfo) {
                                comunasMap.set(communeId, comunaInfo)
                            }
                        }
                    }
                })
            }
        }
        
        const result = Array.from(comunasMap.values()).sort((a, b) => a.name.localeCompare(b.name))
        return result
    }

    /**
     * Obtiene información de comuna desde todas las ubicaciones
     * @param {number} communeId - ID de la comuna
     * @param {Array} allLocations - Lista completa de ubicaciones
     * @returns {Object|null} Información de la comuna
     */
    getComunaInfoFromAllLocations(communeId, allLocations) {
        
        const locationWithComuna = allLocations.find(location => 
            location.Commune && location.Commune.id === communeId
        )
        
        if (locationWithComuna && locationWithComuna.Commune) {
            const result = {
                id: locationWithComuna.Commune.id,
                name: locationWithComuna.Commune.name,
                regionId: locationWithComuna.Commune.regionId,
                regionName: locationWithComuna.Region ? locationWithComuna.Region.name : ''
            }
            return result
        }
        
        return null
    }

    /**
     * Obtiene las sedes filtradas por comuna desde los datos del programa
     * @param {Array} programData - Datos del programa con ubicaciones
     * @param {number} comunaId - ID de la comuna
     * @returns {Array} Lista de sedes de la comuna
     */
    getSedesByComunaFromProgram(programData, comunaId) {
        if (!programData || programData.length === 0) return []
        
        const program = programData[0] // Tomamos el primer programa
        if (!program.Locations) return []
        
        return program.Locations
            .filter(location => location.CommuneId === comunaId)
            .map(location => ({
                id: location.id,
                pdvId: location.pdvId,
                name: location.name,
                displayName: location.displayName,
                address: location.address,
                phone: location.phone,
                phone2: location.phone2,
                businessHours: location.businessHours,
                commerceCode: location.commerceCode,
                comunaId: location.CommuneId,
                regionId: location.RegionId
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
    }

    /**
     * Obtiene las sedes filtradas por comuna
     * @param {Array} locations - Lista de ubicaciones
     * @param {number} comunaId - ID de la comuna
     * @returns {Array} Lista de sedes de la comuna
     */
    getSedesByComuna(locations, comunaId) {
        return locations
            .filter(location => location.Commune && location.Commune.id === comunaId)
            .map(location => ({
                id: location.id,
                pdvId: location.pdvId,
                name: location.name,
                displayName: location.displayName,
                address: location.address,
                phone: location.phone,
                phone2: location.phone2,
                businessHours: location.businessHours,
                commerceCode: location.commerceCode,
                comunaId: location.Commune.id,
                comunaName: location.Commune.name,
                cityId: location.City ? location.City.id : null,
                cityName: location.City ? location.City.name : null,
                regionId: location.Region ? location.Region.id : null,
                regionName: location.Region ? location.Region.name : null
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
    }

    /**
     * Obtiene información de una sede específica
     * @param {Array} locations - Lista de ubicaciones
     * @param {number} sedeId - ID de la sede
     * @returns {Object|null} Información de la sede
     */
    getSedeInfo(locations, sedeId) {
        const sede = locations.find(location => location.id === sedeId)
        if (!sede) return null
        
        return {
            id: sede.id,
            pdvId: sede.pdvId,
            name: sede.name,
            displayName: sede.displayName,
            shortName: getLocationDisplayName(sede).replace('Preuniversitario ', ''),
            subtitle: getLocationDisplayName(sede).includes('Preuniversitario') ? 'Preuniversitario' : 'Centro de Estudios',
            address: sede.address,
            phone: sede.phone,
            phone2: sede.phone2,
            businessHours: sede.businessHours,
            commerceCode: sede.commerceCode,
            comunaId: sede.Commune ? sede.Commune.id : null,
            comunaName: sede.Commune ? sede.Commune.name : null,
            cityId: sede.City ? sede.City.id : null,
            cityName: sede.City ? sede.City.name : null,
            regionId: sede.Region ? sede.Region.id : null,
            regionName: sede.Region ? sede.Region.name : null
        }
    }

    /**
     * Obtiene los cursos disponibles desde los datos del programa
     * @param {Array} programData - Datos del programa
     * @returns {Array} Lista de cursos disponibles
     */
    getAvailableCoursesFromProgram(programData) {
        if (!programData || programData.length === 0) {
            return this.getDefaultCourses()
        }
        
        // Por ahora retornamos cursos por defecto
        // En el futuro esto podría venir de los datos del programa
        return this.getDefaultCourses()
    }

    /**
     * Obtiene los cursos por defecto
     * @returns {Array} Lista de cursos por defecto
     */
    getDefaultCourses() {
        return {
            leftColumn: [
                {
                    id: 'lenguaje',
                    name: 'Lenguaje',
                    availableDays: [
                        {
                            id: 'lunes-miercoles',
                            name: 'Lunes y Miércoles',
                            weekDays: ['monday', 'wednesday'],
                            timeSlots: [
                                { id: 'morning', time: '08:00 - 10:00' },
                                { id: 'afternoon', time: '10:00 - 12:00' }
                            ]
                        },
                        {
                            id: 'martes-jueves',
                            name: 'Martes y Jueves',
                            weekDays: ['tuesday', 'thursday'],
                            timeSlots: [
                                { id: 'evening', time: '14:00 - 16:00' },
                                { id: 'night', time: '18:00 - 20:00' }
                            ]
                        }
                    ]
                },
                {
                    id: 'matematica',
                    name: 'Matemática',
                    availableDays: [
                        {
                            id: 'lunes-miercoles',
                            name: 'Lunes y Miércoles',
                            weekDays: ['monday', 'wednesday'],
                            timeSlots: [
                                { id: 'morning', time: '08:00 - 10:00' },
                                { id: 'afternoon', time: '10:00 - 12:00' }
                            ]
                        }
                    ]
                }
            ],
            rightColumn: [
                {
                    id: 'fisica',
                    name: 'Física',
                    availableDays: [
                        {
                            id: 'martes-jueves',
                            name: 'Martes y Jueves',
                            weekDays: ['tuesday', 'thursday'],
                            timeSlots: [
                                { id: 'evening', time: '14:00 - 16:00' },
                                { id: 'night', time: '18:00 - 20:00' }
                            ]
                        }
                    ]
                }
            ]
        }
    }
}

export default new LocationsService()
